import { useTranslation } from 'react-i18next';
import { editorTabs, isKb, keyToggles, tagsKeyOf, visibleFields } from './form/formRules';
import { groupEdits } from './form/groupEdits';
import { newGroupId } from './form/newGroupId';
import { topEdits } from './form/topEdits';
import { useGroupName } from './form/useGroupName';
import { FormGroupEditor } from './FormGroupEditor';
import { FormHiddenCard } from './FormHiddenCard';
import { FormPreview } from './FormPreview';
import { FormSwitchCard } from './FormSwitchCard';
import type { ISettingsFormEditorProps } from './ISettingsFormEditorProps';

/**
 * «Форма» — редактор форми типу записів простору за прототипом v4 (pgCustForm): «Верх форми» (статус, пріоритет, термін у ключовому
 * рядку — у базі знань лише статус; теги й опис під заголовком) і «Вкладки» перемикачами, «Групи» (назва, поля, ширина, група поля,
 * порядок, «Увесь рядок», «+ Додати групу», вилучення), «Приховані поля»; праворуч — «Перегляд». Кожна зміна одразу зберігається
 * перевизначенням простору (ADR-023, If-Match).
 */
export function SettingsFormEditor({ rt, editor }: ISettingsFormEditorProps) {
  const { t, i18n } = useTranslation();
  const groupName = useGroupName(rt.key);
  const { form, apply, canEdit } = editor;
  const fields = visibleFields(rt);
  const has = (k: string) => fields.some((f) => f.key === k);
  const tags = tagsKeyOf(fields);
  const kb = isKb(rt);
  const top = [
    ...keyToggles.filter((k) => has(k) && !(kb && k !== 'status')).map((k) => ({ key: k, label: t(`modSettings.form.keyToggle.${k}`), on: form.key[k], onToggle: () => apply((f) => topEdits.key(f, k)) })),
    ...(tags ? [{ key: 'tags', label: t('modSettings.form.keyToggle.tags'), on: form.showTags, onToggle: () => apply((f) => topEdits.tags(f, tags)) }] : []),
    ...(!kb && has('description') ? [{ key: 'description', label: t('modSettings.form.keyToggle.description'), on: form.showDesc, onToggle: () => apply((f) => topEdits.description(f)) }] : []),
  ];
  const tabs = editorTabs.map((k) => ({ key: k, label: t(`recordPage.tabs.${k}`), on: form.tabs[k], onToggle: () => apply((f) => topEdits.tab(f, k)) }));
  const names = form.groups.map((g) => ({ id: g.id, name: groupName(g) }));
  const newTitle = { en: i18n.getFixedT('en')('modSettings.form.newGroup'), uk: i18n.getFixedT('uk')('modSettings.form.newGroup') };
  return (
    <div className="st-fe-layout">
      <div className="st-fe-main">
        <div className="st-grid st-fe-pair">
          <FormSwitchCard title={t('modSettings.form.top')} items={top} disabled={!canEdit} />
          <FormSwitchCard title={t('modSettings.form.tabs')} note={t('modSettings.form.tabsNote')} items={tabs} disabled={!canEdit} />
        </div>
        <section className="st-card st-fe">
          <span className="st-kicker">{t('modSettings.form.groups')}</span>
          <div className="st-fe__groups">
            {form.groups.map((group, i) => (
              <FormGroupEditor key={group.id} rt={rt} group={group} index={i} total={form.groups.length} name={names[i]!.name} groups={names} editor={editor} />
            ))}
            <button type="button" className="st-btn st-btn--dashed" disabled={!canEdit} onClick={() => apply((f) => groupEdits.add(f, newGroupId(), newTitle))}>{t('modSettings.form.addGroup')}</button>
          </div>
        </section>
        <FormHiddenCard rt={rt} groups={names} editor={editor} />
      </div>
      <FormPreview rt={rt} editor={editor} />
    </div>
  );
}
